import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useListContractsTimeline } from "@workspace/api-client-react";
import type { ContractTimelineItem } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { ChevronDown, ChevronRight, Calendar as CalendarIcon, ZoomIn, ZoomOut } from "lucide-react";
import { format, differenceInCalendarDays } from "date-fns";

type ZoomLevel = 1 | 3 | 5;

const STATUS_COLORS: Record<ContractTimelineItem["status"], { bar: string; border: string; text: string; label: string }> = {
  active: { bar: "bg-emerald-500/85", border: "border-emerald-700", text: "text-white", label: "Active" },
  pending: { bar: "bg-amber-400/85", border: "border-amber-600", text: "text-amber-950", label: "Pending" },
  expired: { bar: "bg-slate-400/70", border: "border-slate-600", text: "text-white", label: "Expired" },
  terminated: { bar: "bg-rose-500/80", border: "border-rose-700", text: "text-white", label: "Terminated" },
};

const ROW_HEIGHT = 36;
const BAR_HEIGHT = 22;
const LEFT_AXIS_WIDTH = 220;
const PX_PER_DAY_BY_ZOOM: Record<ZoomLevel, number> = {
  1: 3,
  3: 1.1,
  5: 0.7,
};

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d: Date, n: number) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function startOfYear(d: Date) { return new Date(d.getFullYear(), 0, 1); }
function addYears(d: Date, n: number) { return new Date(d.getFullYear() + n, 0, 1); }

export default function Timeline() {
  const [, setLocation] = useLocation();
  const [zoom, setZoom] = useState<ZoomLevel>(3);
  const [sportFilter, setSportFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expiringMonths, setExpiringMonths] = useState<string>("none");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const { data: contracts, isLoading } = useListContractsTimeline();

  const sports = useMemo(() => {
    if (!contracts) return [];
    return Array.from(new Set(contracts.map((c) => c.sport))).sort();
  }, [contracts]);

  const filtered = useMemo(() => {
    if (!contracts) return [];
    return contracts.filter((c) => {
      if (sportFilter !== "all" && c.sport !== sportFilter) return false;
      if (statusFilter !== "all") {
        if (statusFilter === "active_pending") {
          if (c.status !== "active" && c.status !== "pending") return false;
        } else if (c.status !== statusFilter) return false;
      } else {
        if (c.status !== "active" && c.status !== "pending") return false;
      }
      return true;
    });
  }, [contracts, sportFilter, statusFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, ContractTimelineItem[]>();
    for (const c of filtered) {
      const arr = map.get(c.sport) ?? [];
      arr.push(c);
      map.set(c.sport, arr);
    }
    for (const [, arr] of map) {
      arr.sort((a, b) => a.startDate.localeCompare(b.startDate) || a.coachName.localeCompare(b.coachName));
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // Greedy lane packing: assign each contract to the first lane where it
  // doesn't overlap any previously placed bar. Result is a list of lanes
  // (each lane is an array of contracts) per sport.
  const packed = useMemo(() => {
    return grouped.map(([sport, items]) => {
      const lanes: { end: number; contracts: ContractTimelineItem[] }[] = [];
      for (const c of items) {
        const start = new Date(c.startDate).getTime();
        const end = new Date(c.endDate).getTime();
        let placed = false;
        for (const lane of lanes) {
          if (start >= lane.end) {
            lane.contracts.push(c);
            lane.end = end;
            placed = true;
            break;
          }
        }
        if (!placed) {
          lanes.push({ end, contracts: [c] });
        }
      }
      return { sport, items, lanes: lanes.map((l) => l.contracts) };
    });
  }, [grouped]);

  const today = useMemo(() => new Date(), []);

  const { rangeStart, rangeEnd, pxPerDay, totalWidth } = useMemo(() => {
    const pxPerDay = PX_PER_DAY_BY_ZOOM[zoom];
    let minStart = startOfYear(today);
    let maxEnd = addYears(startOfYear(today), zoom);

    for (const c of filtered) {
      const s = new Date(c.startDate);
      const e = new Date(c.endDate);
      if (s < minStart) minStart = startOfMonth(s);
      if (e > maxEnd) maxEnd = addMonths(startOfMonth(e), 1);
    }
    minStart = addMonths(minStart, -1);
    const totalDays = Math.max(1, differenceInCalendarDays(maxEnd, minStart));
    return {
      rangeStart: minStart,
      rangeEnd: maxEnd,
      pxPerDay,
      totalWidth: Math.ceil(totalDays * pxPerDay),
    };
  }, [filtered, zoom, today]);

  const expiringDays = expiringMonths === "none" ? null : parseInt(expiringMonths, 10) * 30;

  const xForDate = (d: Date) => differenceInCalendarDays(d, rangeStart) * pxPerDay;

  const months = useMemo(() => {
    const arr: { label: string; x: number; isYearStart: boolean }[] = [];
    let cur = startOfMonth(rangeStart);
    while (cur < rangeEnd) {
      arr.push({
        label: format(cur, cur.getMonth() === 0 ? "MMM yyyy" : "MMM"),
        x: xForDate(cur),
        isYearStart: cur.getMonth() === 0,
      });
      cur = addMonths(cur, 1);
    }
    return arr;
  }, [rangeStart, rangeEnd, pxPerDay]);

  const toggleSport = (sport: string) =>
    setCollapsed((prev) => ({ ...prev, [sport]: !prev[sport] }));

  const totalRows = packed.reduce(
    (sum, { sport, lanes }) => sum + 1 + (collapsed[sport] ? 0 : lanes.length),
    0,
  );
  const chartHeight = Math.max(totalRows * ROW_HEIGHT, ROW_HEIGHT * 4);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Contract Timeline</h1>
          <p>Visualize active and pending contracts across all sports.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={zoom === 1 ? "default" : "outline"}
            size="sm"
            onClick={() => setZoom(1)}
          >
            1Y
          </Button>
          <Button
            variant={zoom === 3 ? "default" : "outline"}
            size="sm"
            onClick={() => setZoom(3)}
          >
            3Y
          </Button>
          <Button
            variant={zoom === 5 ? "default" : "outline"}
            size="sm"
            onClick={() => setZoom(5)}
          >
            5Y
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button
            variant="outline"
            size="icon"
            onClick={() => setZoom((z) => (z === 5 ? 3 : 1))}
            disabled={zoom === 1}
            aria-label="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setZoom((z) => (z === 1 ? 3 : 5))}
            disabled={zoom === 5}
            aria-label="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
            <div className="w-full sm:w-[200px]">
              <Select value={sportFilter} onValueChange={setSportFilter}>
                <SelectTrigger><SelectValue placeholder="All sports" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sports</SelectItem>
                  {sports.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-[200px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Active &amp; pending</SelectItem>
                  <SelectItem value="active_pending">Active &amp; pending</SelectItem>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="pending">Pending only</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-[220px]">
              <Select value={expiringMonths} onValueChange={setExpiringMonths}>
                <SelectTrigger><SelectValue placeholder="Highlight expiring" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No expiring highlight</SelectItem>
                  <SelectItem value="3">Expiring in 3 months</SelectItem>
                  <SelectItem value="6">Expiring in 6 months</SelectItem>
                  <SelectItem value="12">Expiring in 12 months</SelectItem>
                  <SelectItem value="24">Expiring in 24 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 sm:ml-auto text-xs text-muted-foreground flex-wrap">
              {(["active", "pending", "expired", "terminated"] as const).map((s) => (
                <div key={s} className="flex items-center gap-1.5">
                  <span className={`inline-block h-2.5 w-3 rounded-sm ${STATUS_COLORS[s].bar} border ${STATUS_COLORS[s].border}`} />
                  <span>{STATUS_COLORS[s].label}</span>
                </div>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={CalendarIcon}
              title="No contracts to show"
              description="Adjust the filters or create contracts to populate the timeline."
            />
          ) : (
            <>
            <div className="md:hidden space-y-4">
              {packed.map(({ sport, items }) => (
                <div key={sport}>
                  <button
                    type="button"
                    onClick={() => toggleSport(sport)}
                    className="w-full flex items-center gap-1 px-2 py-2 text-left text-sm font-semibold border-b"
                  >
                    {collapsed[sport] ? (
                      <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
                    )}
                    <span className="truncate">{sport}</span>
                    <span className="ml-auto text-xs font-normal text-muted-foreground">{items.length}</span>
                  </button>
                  {!collapsed[sport] && (
                    <ul className="divide-y">
                      {items.map((c) => {
                        const start = new Date(c.startDate);
                        const end = new Date(c.endDate);
                        const colors = STATUS_COLORS[c.status];
                        const daysRemaining = differenceInCalendarDays(end, today);
                        const isExpiringHighlight =
                          expiringDays != null && daysRemaining >= 0 && daysRemaining <= expiringDays;
                        return (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() => setLocation(`/contracts/${c.id}`)}
                              className={`w-full text-left px-2 py-3 hover:bg-muted/50 ${isExpiringHighlight ? "bg-amber-50" : ""}`}
                            >
                              <div className="flex items-center gap-2">
                                <span className={`inline-block h-2.5 w-3 rounded-sm ${colors.bar} border ${colors.border} flex-shrink-0`} />
                                <span className="font-medium text-sm truncate">{c.coachName}</span>
                                <span className="ml-auto text-[11px] uppercase tracking-wide text-muted-foreground">{colors.label}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {format(start, "MMM yyyy")} → {format(end, "MMM yyyy")}
                              </div>
                              <div className="text-xs mt-0.5">
                                {daysRemaining >= 0
                                  ? `${daysRemaining.toLocaleString()} days remaining`
                                  : `Ended ${Math.abs(daysRemaining).toLocaleString()} days ago`}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            <div className="hidden md:block border rounded-md overflow-hidden">
              <div className="flex">
                <div
                  className="flex-shrink-0 border-r bg-muted/30"
                  style={{ width: LEFT_AXIS_WIDTH }}
                >
                  <div className="h-10 border-b px-3 flex items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Sport / Coach
                  </div>
                  <div style={{ height: chartHeight }}>
                    {packed.map(({ sport, items, lanes }) => (
                      <div key={sport}>
                        <button
                          type="button"
                          onClick={() => toggleSport(sport)}
                          className="w-full flex items-center gap-1 px-2 text-left text-sm font-semibold hover:bg-muted/50"
                          style={{ height: ROW_HEIGHT }}
                        >
                          {collapsed[sport] ? (
                            <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
                          )}
                          <span className="truncate">{sport}</span>
                          <span className="ml-auto text-xs font-normal text-muted-foreground">
                            {items.length}
                            {lanes.length > 1 ? ` / ${lanes.length} rows` : ""}
                          </span>
                        </button>
                        {!collapsed[sport] && lanes.map((_, laneIdx) => (
                          <div
                            key={laneIdx}
                            className="px-3 pl-7 text-[11px] truncate text-muted-foreground/70 flex items-center"
                            style={{ height: ROW_HEIGHT }}
                          >
                            Row {laneIdx + 1}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex-1 overflow-x-auto">
                  <div style={{ width: totalWidth, position: "relative" }}>
                    <div className="h-10 border-b relative bg-muted/30">
                      {months.map((m, idx) => (
                        <div
                          key={idx}
                          className={`absolute top-0 h-full flex items-end pb-1 text-[10px] ${m.isYearStart ? "font-semibold text-foreground" : "text-muted-foreground"}`}
                          style={{ left: m.x, transform: "translateX(-1px)" }}
                        >
                          <div
                            className={`absolute top-0 bottom-0 w-px ${m.isYearStart ? "bg-border" : "bg-border/40"}`}
                          />
                          <span className="ml-1 whitespace-nowrap">{m.label}</span>
                        </div>
                      ))}
                    </div>
                    <div className="relative" style={{ height: chartHeight }}>
                      {months.map((m, idx) => (
                        <div
                          key={idx}
                          className={`absolute top-0 bottom-0 w-px ${m.isYearStart ? "bg-border" : "bg-border/30"}`}
                          style={{ left: m.x }}
                        />
                      ))}
                      {today >= rangeStart && today <= rangeEnd && (
                        <div
                          className="absolute top-0 bottom-0 w-0.5 bg-blue-600 z-10"
                          style={{ left: xForDate(today) }}
                        >
                          <div className="absolute -top-0 left-1 text-[10px] font-semibold text-blue-700 bg-white/90 px-1 rounded-sm">
                            Today
                          </div>
                        </div>
                      )}
                      {(() => {
                        const out: React.ReactNode[] = [];
                        let rowIndex = 0;
                        for (const { sport, lanes } of packed) {
                          const sportRow = rowIndex;
                          rowIndex += 1;
                          out.push(
                            <div
                              key={`${sport}-row`}
                              className="absolute left-0 right-0 border-b border-border/30 bg-muted/10"
                              style={{ top: sportRow * ROW_HEIGHT, height: ROW_HEIGHT }}
                            />,
                          );
                          if (collapsed[sport]) continue;
                          for (let laneIdx = 0; laneIdx < lanes.length; laneIdx++) {
                            const laneRow = rowIndex + laneIdx;
                            out.push(
                              <div
                                key={`${sport}-lane-${laneIdx}-bg`}
                                className="absolute left-0 right-0 border-b border-border/20"
                                style={{ top: laneRow * ROW_HEIGHT, height: ROW_HEIGHT }}
                              />,
                            );
                            for (const c of lanes[laneIdx]) {
                              const start = new Date(c.startDate);
                              const end = new Date(c.endDate);
                              const x = xForDate(start);
                              const w = Math.max(2, xForDate(end) - x);
                              const colors = STATUS_COLORS[c.status];
                              const daysRemaining = differenceInCalendarDays(end, today);
                              const isExpiringHighlight =
                                expiringDays != null && daysRemaining >= 0 && daysRemaining <= expiringDays;
                              const top = laneRow * ROW_HEIGHT + (ROW_HEIGHT - BAR_HEIGHT) / 2;
                              const showLabel = w > 80;
                              out.push(
                                <Tooltip key={c.id}>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      onClick={() => setLocation(`/contracts/${c.id}`)}
                                      className={`absolute rounded border ${colors.bar} ${colors.border} ${colors.text} ${isExpiringHighlight ? "ring-2 ring-amber-500 ring-offset-1" : ""} hover:brightness-110 transition shadow-sm overflow-hidden`}
                                      style={{ left: x, top, width: w, height: BAR_HEIGHT }}
                                    >
                                      {showLabel && (
                                        <span className="px-2 text-xs font-medium whitespace-nowrap leading-[22px]">
                                          {c.coachName} · {format(end, "yyyy")}
                                        </span>
                                      )}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    <div className="space-y-0.5">
                                      <div className="font-semibold">{c.coachName} — {c.sport}</div>
                                      <div>{format(start, "MMM d, yyyy")} → {format(end, "MMM d, yyyy")}</div>
                                      <div>
                                        Salary:{" "}
                                        {typeof c.annualSalary === "number"
                                          ? `$${c.annualSalary.toLocaleString()}/yr`
                                          : "•••"}
                                      </div>
                                      <div>
                                        Buyout:{" "}
                                        {(c as { financialsMasked?: boolean }).financialsMasked
                                          ? "•••"
                                          : c.buyoutClause != null
                                          ? `$${c.buyoutClause.toLocaleString()}`
                                          : "—"}
                                      </div>
                                      <div>
                                        {daysRemaining >= 0
                                          ? `${daysRemaining.toLocaleString()} days remaining`
                                          : `Ended ${Math.abs(daysRemaining).toLocaleString()} days ago`}
                                      </div>
                                      <div className="capitalize text-muted-foreground">Status: {c.status}</div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>,
                              );
                            }
                          }
                          rowIndex += lanes.length;
                        }
                        return out;
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
